import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useCatalog } from './CatalogContext'
import { ChangelogItem, ChangelogSource } from '../types/catalog'

type ChangelogState = {
  entries: ChangelogItem[]
  loading: boolean
  refresh: () => void
}

const ChangelogContext = createContext<ChangelogState>({
  entries: [],
  loading: false,
  refresh: () => {},
})

export function ChangelogProvider({ children }: { children: React.ReactNode }) {
  const { catalog } = useCatalog()
  const [entries, setEntries] = useState<ChangelogItem[]>([])
  const [loading, setLoading] = useState(false)

  const fetchChangelogs = useCallback(async (sources: ChangelogSource[]) => {
    let allEntries: ChangelogItem[] = []

    for (const source of sources) {
      if (source.enabled === false) continue

      // Add static entries if they exist
      if (source.static) {
        allEntries = [...allEntries, ...source.static]
      }

      // Fetch from external URL if provided
      if (source.url) {
        try {
          const res = await fetch(source.url)
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
          const data = await res.json()
          // Expecting an array of items or a specific format
          const items = Array.isArray(data) ? data : (data.items || [])
          allEntries = [...allEntries, ...items]
        } catch (e) {
          console.error(`Failed to fetch changelogs for ${source.name}`, e)
        }
      }
    }

    // Sort by date descending
    return allEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [])

  const refresh = useCallback(async () => {
    if (!catalog?.changelogs) {
        setEntries([])
        return
    }
    setLoading(true)
    const data = await fetchChangelogs(catalog.changelogs)
    setEntries(data)
    setLoading(false)
  }, [catalog, fetchChangelogs])

  useEffect(() => {
    refresh()
  }, [refresh])

  return (
    <ChangelogContext.Provider value={{ entries, loading, refresh }}>
      {children}
    </ChangelogContext.Provider>
  )
}

export const useChangelogs = () => useContext(ChangelogContext)
